import createUniversalCookie from './create-universal-cookie';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import { AUTHENTICATION_VARIRABLES } from './constants/authentication_variables';
import { IDENTITY_TYPES } from './constants/identity_types';

let errorHandlerFunc;

export const AuthenticationErrorCodes = {
    MISSING_OR_MALFORMED_COOKIE: 'missingOrMalformedCookie',
    NOT_SIGNED_IN: 'notSignedIn',
    REFRESH_TOKEN_FAILED: 'refreshTokenFailed',
    AUTH0_REFRESH_FAILED: 'auth0RefreshFailed',
    USER_IS_INACTIVE: 'userIsInactive'
};

const gydaCookie = {
  token: "gydaToken",
  remember: "gydaRemember",
};

let instanceResolve;
let instance = new Promise(resolve => {
    instanceResolve = resolve;
});

export class AuthenticationClient {
    constructor({ baseUri, domain, identityUri, cookieDomain, auth0_domain, auth0_audience, auth0_client_id, logoutUrl }) {
        this.baseUri = baseUri;
        this.domain = domain
        this.identityUri = identityUri;
        this.cookieDomain = cookieDomain;
        this.auth0_domain = auth0_domain;
        this.auth0_audience = auth0_audience;
        this.auth0_client_id = auth0_client_id;
        this.logoutUrl = logoutUrl;
        this.token = null;
        this.userCompanyPromise = null;
        this.initialized = false;

        this._cookie = createUniversalCookie();

        this.init().then(() => {
            instanceResolve(this);
        });
    }

    async init() {
        if(this.initialized) {
            return;
        }
        this.initialized = true;

        this.token = this._getInitialToken();
        this.auth0Client = await this._getAuth0Client();
    }

    async getCurrentUserId() {
        return await this.getCurrentIdentityInformation(IDENTITY_TYPES.USER);
    }

    async getCurrentCompanyId() {
        return await this.getCurrentIdentityInformation(IDENTITY_TYPES.COMPANY);
    }

    async getActiveWorkspaceId() {
        return await this.getCurrentIdentityInformation(IDENTITY_TYPES.WORKSPACE);
    }

    async getCurrentIdentityInformation(identityType) {
        if(!this.userCompanyPromise) {
            this.userCompanyPromise = fetch(`${this.identityUri}/`, {
                headers: {
                    Authorization: this.token,
                    'x-client-name': AUTHENTICATION_VARIRABLES.CLIENT_NAME
                }
            })
                .then(response => {
                    if(!response.ok) {
                        throw new Error(`AuthenticationClient - bad response from identity.`);
                    }
                    return response.json();
                })
                .catch(() => {
                    this.userCompanyPromise = null;
                    throw new Error(`AuthenticationClient failed to get ${identityType} information.`);
                });
        }
        return this.userCompanyPromise
            .then(payload => {
                switch(identityType) {
                    case IDENTITY_TYPES.USER:
                        return payload.user._id;
                    case IDENTITY_TYPES.COMPANY:
                        return payload.company._id;
                    case IDENTITY_TYPES.WORKSPACE:
                        return payload.companyMembership.activeWorkspaceId;
                }
            });
    }

    async getToken() {
        if(!this.token) {
            this.throwError(AuthenticationErrorCodes.NOT_SIGNED_IN);
        }

        return this.token;
    }

    /**
     * Remove the Gyda cookies and call the gaf-idp /logout endpoint to log the user out. Right now this is only being
     * used for user inactivity.
     *
     * @return {void}
     */
    logout(status) {
        // Remove old cookie references and redirect to logout page.
        this._cookie.remove(gydaCookie.token, { domain: this.cookieDomain });
        this._cookie.remove(gydaCookie.remember, { domain: this.cookieDomain });
        this.token = null;

        this.navigateToLogout(status);
    }

    /**
     * Convenience function for navigating to logout URL. This is exposed via a function for testing purposes!
     * Will only tag status on the url if passed in
     */
    navigateToLogout(status) {
        window.location.href = status
            ? `${this.logoutUrl}?redirectTo=${encodeURIComponent(window.location.href)}&status=${status}`
            : `${this.logoutUrl}?redirectTo=${encodeURIComponent(window.location.href)}`;
    }

    navigateToWorkspace(url) {
        window.location.assign(url);
    }

    async _getAuth0Client() {
        if(!this.auth0Client) {
            this.auth0Client = await createAuth0Client({
                domain: this.auth0_domain,
                authorizationParams: {
                    audience: this.auth0_audience,
                },
                clientId: this.auth0_client_id,
                useRefreshTokens: true,
                useRefreshTokensFallback: true // Adding this to sync behavior between version 1.x and 2.x of Auth0 client. See here: https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/27
            });
        }

        return this.auth0Client;
    }

    async refreshAuth0Token() {
        try {
            const client = await this._getAuth0Client();
            return await client.getTokenSilently();
        } catch (e) {
            this.throwError(AuthenticationErrorCodes.AUTH0_REFRESH_FAILED);
        }

    }

    _getInitialToken() {
        try {
            const cookieValue = this._cookie.get('gydaToken', {domain: this.cookieDomain});
            return cookieValue.token;
        } catch(error) {
            this.throwError(AuthenticationErrorCodes.MISSING_OR_MALFORMED_COOKIE);
        }
    }

    throwError(reason) {
        if(errorHandlerFunc) {
            errorHandlerFunc(reason);
        } else {
            throw new Error(`AuthenticationClient - No error handler function registered. ErrorCode: ${reason}`);
        }
    }

    static onError(passedErrorHandlerFunc) {
        if(errorHandlerFunc) {
            throw new Error('AuthenticationClient.onError can only be invoked once.');
        } else {
            errorHandlerFunc = passedErrorHandlerFunc;
        }
    }

    static async getInstance() {
        return instance;
    }
}

export function __clearSingletonForTests() {
    if (typeof describe !== 'function') {
        throw new Error('This is for testing purposes only!!');
    }

    AuthenticationClient.initialized = false;
    instance = new Promise(resolve => {
        instanceResolve = resolve;
    });
    errorHandlerFunc = undefined;
}
